import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight, GitCompareArrows,
    MapPin, Sparkles, Gauge, Activity, MessageCircle
} from "lucide-react";
import api, { resolveAsset } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import LeadModal from "@/components/site/LeadModal";
import HomeBikeFinder from "@/components/site/HomeBikeFinder";
import HomeBrandsSection from "@/components/site/HomeBrandsSection";
import HomeHowItWorks from "@/components/site/HomeHowItWorks";
import HomeGuaranteeSection from "@/components/site/HomeGuaranteeSection";
import HomeUpcomingStrip from "@/components/site/HomeUpcomingStrip";
import ScrollCue from "@/components/site/ScrollCue";
import NearestOutletWidget from "@/components/site/NearestOutletWidget";
import PageSeo from "@/components/site/PageSeo";
import { resolveWhatsAppDigits, whatsappUrl } from "@/lib/contact";

/* ============== COUNT-UP NUMBER ============== */
const CountUp = ({ to, suffix = "", duration = 1.6 }) => {
    const [v, setV] = useState(0);
    const ref = useRef(null);
    useEffect(() => {
        let start, raf, started = false;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !started) {
                started = true;
                const tick = (t) => {
                    if (!start) start = t;
                    const p = Math.min((t - start) / (duration * 1000), 1);
                    setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
                    if (p < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
            }
        }, { threshold: 0.5 });
        if (ref.current) obs.observe(ref.current);
        return () => { obs.disconnect(); cancelAnimationFrame(raf); };
    }, [to, duration]);
    return <span ref={ref}>{v}{suffix}</span>;
};

/* ============== FULL-BLEED HERO STAGE ============== */
const HeroStage = ({ images, fallbackBikes, index, setIndex }) => {
    const slides = useMemo(() => {
        if (images && images.length > 0) return images.map(resolveAsset);
        return (fallbackBikes || []).slice(0, 5).map((b) => resolveAsset(b.image)).filter(Boolean);
    }, [images, fallbackBikes]);

    useEffect(() => {
        if (slides.length < 2) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4500);
        return () => clearInterval(id);
    }, [slides.length, setIndex]);

    if (slides.length === 0) {
        return <div className="absolute inset-0 bg-[#0A2540]" aria-hidden />;
    }

    return (
        <div className="absolute inset-0" data-testid="hero-carousel">
            <AnimatePresence mode="sync" initial={false}>
                <motion.img
                    key={slides[index]}
                    src={slides[index]}
                    alt=""
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover object-[center_40%] sm:object-center"
                    draggable={false}
                    loading="eager"
                    decoding="async"
                />
            </AnimatePresence>
            {/* Readability overlays — left-weighted for copy */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#050d18]/95 via-[#0A2540]/72 to-[#0A2540]/25 sm:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050d18]/90 via-transparent to-[#0A2540]/40" />
            <div className="absolute inset-0 opacity-[0.12]" style={{
                backgroundImage: "linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)",
                backgroundSize: "72px 72px",
            }} />
        </div>
    );
};

/* ============== SALES SHORTCUTS ============== */
const SALES_SHORTCUTS = [
    { icon: Gauge, label: "All Bikes", desc: "Browse by budget & brand", href: "/bikes" },
    { icon: GitCompareArrows, label: "Compare", desc: "Specs side by side", href: "/compare" },
    { icon: Activity, label: "EMI Calculator", desc: "Plan your down payment", href: "/emi" },
    { icon: MapPin, label: "Find Outlet", desc: "Test ride near you", href: "/dealers" },
];
const SalesShortcuts = () => (
    <section className="bg-white border-t border-b border-zinc-100 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-zinc-100" data-testid="sales-shortcuts">
        {SALES_SHORTCUTS.map((s, i) => {
            const Icon = s.icon;
            const inner = (
                <>
                    <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-[#E31837]/10 group-hover:bg-white/10 transition-colors blur-2xl" />
                    <Icon size={26} className="mx-auto text-[#0A2540] group-hover:text-white transition-all duration-500 group-hover:scale-125 sm:w-[30px] sm:h-[30px]" strokeWidth={1.5} />
                    <h4 className="mt-2 sm:mt-3 text-[10px] sm:text-xs font-black tracking-wide uppercase text-[#0A2540] group-hover:text-white relative z-10">{s.label}</h4>
                    <p className="mt-1 text-[9px] sm:text-[10px] text-zinc-400 group-hover:text-white/80 transition-colors relative z-10 line-clamp-2">{s.desc}</p>
                </>
            );
            const cls = "p-5 sm:p-8 text-center group hover:bg-[#E31837] transition-colors duration-500 cursor-pointer relative overflow-hidden block w-full h-full min-h-[100px] sm:min-h-0";
            return (
                <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -4 }}
                    data-testid={`sales-shortcut-${i}`}
                >
                    <Link to={s.href} className={cls}>{inner}</Link>
                </motion.div>
            );
        })}
    </section>
);

/* ============== TRUST BLOCK ============== */
const DEFAULT_TRUST_TABS = [
    { key: "standard", label: "Our Standard", body: "Servall is a multi-brand two-wheeler retailer across Gujarat and beyond. Clear pricing, authorized showrooms, and sales support — from first enquiry to handover." },
    { key: "testride", label: "Test Ride", body: "Shortlist bikes online, then visit your nearest outlet for a test ride and a full on-road quote with RTO, insurance, and current offers." },
    { key: "pricing", label: "Transparent Pricing", body: "Ex-showroom prices on the site. Ask for a full on-road quote — no surprise add-ons after you walk in." },
];

const TrustBlock = ({ pages = {}, stats = {}, cms = {} }) => {
    const TRUST_TABS = [
        { key: "standard", label: pages.trust_tab1_label || DEFAULT_TRUST_TABS[0].label, body: pages.trust_tab1_body || DEFAULT_TRUST_TABS[0].body },
        { key: "testride", label: pages.trust_tab2_label || DEFAULT_TRUST_TABS[1].label, body: pages.trust_tab2_body || DEFAULT_TRUST_TABS[1].body },
        { key: "pricing", label: pages.trust_tab3_label || DEFAULT_TRUST_TABS[2].label, body: pages.trust_tab3_body || DEFAULT_TRUST_TABS[2].body },
    ];
    const [tab, setTab] = useState("standard");
    const active = TRUST_TABS.find((t) => t.key === tab) ?? TRUST_TABS[0];
    const outlets = pages.trust_use_live_stats !== false ? ((stats || {}).total_dealers || 38) : 38;
    const ridersVal = pages.trust_stat_riders_value ?? 1;
    const ridersLabel = pages.trust_stat_riders_label || "Lakh+ riders";
    return (
        <section className="bg-white border-t border-zinc-200/80 py-12 sm:py-20 relative overflow-hidden" data-testid="trust-section">
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#E31837]/8 blur-3xl pointer-events-none" />
            <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative">
                <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:col-span-5 space-y-4 sm:space-y-5">
                    <p className="text-[11px] font-semibold text-[#E31837] tracking-wide uppercase">{pages.trust_eyebrow || "Why Servall"}</p>
                    {cms.metricsTitle ? (
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A2540] leading-tight whitespace-pre-line">{cms.metricsTitle}</h2>
                    ) : (
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A2540] leading-tight">
                            <CountUp to={outlets} suffix="+" /> outlets.<br />
                            <CountUp to={ridersVal} suffix=" " /> {ridersLabel}.<br />
                            <span className="text-zinc-400">One network.</span>
                        </h2>
                    )}
                    <p className="text-zinc-500 text-xs sm:text-sm max-w-sm">{cms.metricsSub || "Authorized multi-brand retail — compare, get an on-road quote, and book a test ride near you."}</p>
                    <Link to="/dealers" className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wide text-[#E31837] hover:underline" data-testid="trust-dealers-link">
                        Find nearest outlet <ArrowRight size={12} />
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="lg:col-span-7 bg-[#F4F6F8] border border-zinc-200 rounded-[20px] sm:rounded-[28px] p-5 sm:p-8 lg:p-10"
                >
                    <ScrollCue className="flex border-b border-zinc-200 gap-6 text-[11px] font-black uppercase tracking-wide text-zinc-400">
                        {TRUST_TABS.map((t) => (
                            <button key={t.key} onClick={() => setTab(t.key)} className={`pb-4 min-h-[44px] relative whitespace-nowrap transition-colors ${tab === t.key ? "text-[#E31837]" : "hover:text-zinc-700"}`} data-testid={`trust-tab-${t.key}`}>
                                {t.label}
                                {tab === t.key && <motion.span layoutId="trust-underline" className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#E31837]" />}
                            </button>
                        ))}
                    </ScrollCue>
                    <div className="mt-6 min-h-[100px] flex items-start">
                        <AnimatePresence mode="wait">
                            <motion.p
                                key={tab}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="text-zinc-600 text-sm leading-relaxed"
                                data-testid="trust-body"
                            >
                                {active?.body ?? ""}
                            </motion.p>
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

/* ============== CTA BLOCK (inquiry-first) ============== */
const CTABlock = ({ onInquiry, cms = {} }) => {
    const { settings } = useSettings();
    const waHref = whatsappUrl(
        resolveWhatsAppDigits(settings),
        "Hi Servall Bikes, I want today's best on-road quote. Please call me back.",
    );
    return (
        <section id="contact" className="bg-[#0A2540] text-white py-12 sm:py-20 px-4 sm:px-6 relative overflow-hidden" data-testid="cta-block">
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#E31837]/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#E31837]/10 blur-3xl pointer-events-none" />
            <div className="max-w-4xl mx-auto text-center space-y-5 sm:space-y-6 relative z-10">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-[#E31837] mx-auto" />
                <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                    {cms.ctaFooterTitle || "Get your on-road quote today"}
                </motion.h2>
                <p className="text-zinc-300 max-w-xl mx-auto text-xs sm:text-sm md:text-base opacity-90">
                    {cms.ctaFooterSub || "Talk to Servall for the best deal, EMI options, or a test ride at your nearest outlet."}
                </p>
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={onInquiry}
                        className="inline-flex items-center gap-2 bg-[#E31837] text-white font-bold text-xs uppercase tracking-wide px-8 sm:px-10 py-4 rounded-xl shadow-lg shadow-[#E31837]/40 hover:bg-white hover:text-[#0A2540] transition-all duration-300 min-h-[48px]"
                        data-testid="cta-inquiry"
                    >
                        {cms.ctaFooterButton || "Request callback"} <ArrowRight size={14} />
                    </button>
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold text-xs uppercase tracking-wide px-8 py-4 rounded-xl hover:bg-[#1ea952] transition-all min-h-[48px]"
                        data-testid="cta-whatsapp"
                    >
                        <MessageCircle size={16} /> WhatsApp sales
                    </a>
                    <Link
                        to="/dealers"
                        className="inline-flex items-center gap-2 border border-white/30 text-white font-bold text-xs uppercase tracking-wide px-6 py-4 rounded-xl hover:bg-white/10 transition-all min-h-[48px]"
                        data-testid="cta-dealers"
                    >
                        Find outlet
                    </Link>
                </div>
            </div>
        </section>
    );
};

/* ============== MAIN HOMEPAGE ============== */
const HomePage = () => {
    const { settings, pages, stats } = useSettings();
    const [showcase, setShowcase] = useState([]);
    const [showcaseImages, setShowcaseImages] = useState([]);
    const [heroIndex, setHeroIndex] = useState(0);
    const [inquiryOpen, setInquiryOpen] = useState(false);
    const [cms, setCms] = useState({
        heroEyebrow: "Authorized multi-brand outlets",
        heroHeadlineLine1: "Your next ride.",
        heroHeadlineLine2: "Best on-road deal.",
        heroHeadlineAccent: "",
        heroSubcopy: "Compare models, check EMI, and get today's price from a Servall showroom near you.",
        ctaPrimaryLabel: "Browse Bikes",
        ctaSecondaryLabel: "WhatsApp Us",
    });

    const heroRef = useRef(null);
    const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroY = useTransform(heroProgress, [0, 1], [0, 80]);
    const heroFade = useTransform(heroProgress, [0, 0.85], [1, 0.35]);

    const waHref = whatsappUrl(
        resolveWhatsAppDigits(settings),
        "Hi Servall Bikes, I want help choosing a bike and today's best on-road quote.",
    );

    const slideCount = useMemo(() => {
        if (showcaseImages?.length) return showcaseImages.length;
        return Math.min(5, (showcase || []).filter((b) => b.image).length);
    }, [showcaseImages, showcase]);

    const headlineLines = [cms.heroHeadlineLine1, cms.heroHeadlineLine2, cms.heroHeadlineAccent].filter(Boolean);

    useEffect(() => {
        api.get("/config/homepage-layout").then(r => setCms(prev => ({ ...prev, ...r.data }))).catch(() => {});
        api.get("/config/showcase-images").then(r => setShowcaseImages(r.data?.images || [])).catch(() => {});
        api.get("/bikes?is_featured=true&limit=8").then((r) => {
            setShowcase(r.data.items || []);
        });
    }, []);

    return (
        <div className="bg-white text-[#0A2540] overflow-x-hidden">
            <PageSeo pageKey="home" />

            {/* HERO — full-bleed cinematic */}
            <section
                id="buy"
                ref={heroRef}
                className="relative min-h-[92svh] sm:min-h-[88vh] flex flex-col justify-end overflow-hidden bg-[#0A2540] text-white"
            >
                <motion.div style={{ y: heroY }} className="absolute inset-0">
                    <HeroStage images={showcaseImages} fallbackBikes={showcase} index={heroIndex} setIndex={setHeroIndex} />
                </motion.div>

                <motion.div style={{ opacity: heroFade }} className="relative z-10 w-full">
                    <div className="container-x pt-16 pb-10 sm:pt-24 sm:pb-14 md:pb-16">
                        <div className="max-w-xl lg:max-w-2xl" data-testid="hero-left">
                            <motion.p
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="font-display text-[11px] sm:text-xs tracking-[0.35em] uppercase text-white/70 mb-3 sm:mb-4"
                                data-testid="hero-brand"
                            >
                                Servall
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.45 }}
                                className="text-[11px] font-semibold tracking-wide uppercase text-[#FF4D63]"
                                data-testid="hero-eyebrow"
                            >
                                {cms.heroEyebrow}
                            </motion.p>

                            <h1 className="font-display tracking-tight leading-[0.98] mt-3 sm:mt-4 text-[2.65rem] sm:text-5xl md:text-6xl lg:text-[4.25rem]">
                                {headlineLines.map((line, i) => (
                                    <motion.span
                                        key={`${line}-${i}`}
                                        initial={{ opacity: 0, y: 36 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.18 + i * 0.1, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                                        className={`block ${i === headlineLines.length - 1 && headlineLines.length > 1 ? "text-[#E31837]" : "text-white"}`}
                                    >
                                        {line}
                                    </motion.span>
                                ))}
                            </h1>

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="mt-4 sm:mt-5 text-sm sm:text-base text-white/75 leading-relaxed max-w-md"
                            >
                                {cms.heroSubcopy}
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.62 }}
                                className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3"
                            >
                                <Link
                                    to="/#inventory"
                                    className="inline-flex items-center justify-center gap-2 bg-[#E31837] text-white font-bold text-xs uppercase tracking-wide px-8 py-4 rounded-xl hover:bg-white hover:text-[#0A2540] transition-colors min-h-[52px] group"
                                    data-testid="hero-cta-primary"
                                >
                                    {cms.ctaPrimaryLabel}
                                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                </Link>
                                <a
                                    href={waHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold text-xs uppercase tracking-wide px-8 py-4 rounded-xl hover:bg-[#1ea952] transition-colors min-h-[52px]"
                                    data-testid="hero-cta-whatsapp"
                                >
                                    <MessageCircle size={16} /> {cms.ctaSecondaryLabel || "WhatsApp Us"}
                                </a>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.75 }}
                                className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm"
                            >
                                <button type="button" onClick={() => setInquiryOpen(true)} className="text-white/80 hover:text-white underline-offset-4 hover:underline min-h-[44px]" data-testid="hero-cta-callback">
                                    Request callback
                                </button>
                                <Link to="/emi" className="text-white/55 hover:text-white min-h-[44px] inline-flex items-center" data-testid="hero-cta-emi">
                                    EMI calculator
                                </Link>
                                <Link to="/dealers" className="text-white/55 hover:text-white min-h-[44px] inline-flex items-center" data-testid="hero-cta-dealers">
                                    Find outlet
                                </Link>
                            </motion.div>
                        </div>

                        {/* Slide progress */}
                        {slideCount > 1 && (
                            <div className="mt-8 sm:mt-10 flex items-center gap-1.5">
                                {Array.from({ length: slideCount }).map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setHeroIndex(i)}
                                        aria-label={`Slide ${i + 1}`}
                                        className="min-w-[36px] min-h-[36px] flex items-center justify-center"
                                        data-testid={`carousel-dot-${i}`}
                                    >
                                        <span className={`block h-1 rounded-full transition-all duration-500 ${i === heroIndex ? "w-10 bg-[#E31837]" : "w-4 bg-white/35 hover:bg-white/60"}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Stats strip — below primary hero content */}
                    <div className="border-t border-white/10 bg-black/25 backdrop-blur-md">
                        <div className="container-x py-4 sm:py-5 grid grid-cols-3 gap-3 sm:gap-8 max-w-3xl">
                            {[
                                { v: stats.available_bikes ?? stats.total_bikes ?? 45, suffix: "+", label: "Bikes on sale" },
                                { v: stats.total_brands || 20, suffix: "+", label: "Brands" },
                                { v: stats.total_dealers || 38, suffix: "+", label: "Outlets" },
                            ].map((s) => (
                                <div key={s.label}>
                                    <p className="font-display text-xl sm:text-3xl text-white leading-none">
                                        <CountUp to={s.v} suffix={s.suffix} />
                                    </p>
                                    <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/50 font-semibold mt-1.5">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* 1. Browse / filter bikes */}
            <HomeBikeFinder />

            {/* 2. Quick sales actions */}
            <SalesShortcuts />

            {/* 3. Brands we deal with */}
            <HomeBrandsSection pages={pages} />

            {/* 4. Trust / why Servall */}
            <TrustBlock pages={pages} stats={stats} cms={cms} />

            {/* 4b. Pricing & satisfaction guarantee */}
            <HomeGuaranteeSection pages={pages} />

            {/* 5. How to buy */}
            <HomeHowItWorks pages={pages} />

            {/* 6. Nearest outlet */}
            <NearestOutletWidget limit={3} title={pages.home_nearest_title} subtitle={pages.home_nearest_sub} />

            {/* 7. Optional content (off by default) */}
            <HomeUpcomingStrip enabled={pages.home_show_upcoming === true} />

            {/* 8. Final inquiry CTA */}
            <CTABlock onInquiry={() => setInquiryOpen(true)} cms={cms} />

            <LeadModal
                open={inquiryOpen}
                onOpenChange={setInquiryOpen}
                intent="whatsapp_inquiry"
                note="Homepage inquiry — please call back with today's best on-road offers."
            />
        </div>
    );
};

export default HomePage;
